/**
 * Iter 97 — step-by-step mobile Drop-off workflow (spec §8):
 * Mileage → Fuel → Inspection compare → Extra charges → Deposit → Complete Return.
 * Persists via /bookings/{id}/checklist (phase=return) + /return/complete.
 */
import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import Money from "@/components/Money";
import DamageDiagramCanvas from "@/components/inspection/DamageDiagramCanvas";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Trash2, Plus } from "lucide-react";
import { WizardShell, WizardNav, FuelPicker, Row } from "@/components/ops/wizardShared";

const STEPS = ["Mileage", "Fuel", "Inspection", "Charges", "Deposit"];
const CHARGE_KINDS = ["Fuel", "Late return", "Damage", "Cleaning", "Extra mileage", "Other"];

export default function ReturnWizard({ booking, onClose, onDone }) {
  const open = !!booking;
  const [step, setStep] = useState(0);
  const [checklist, setChecklist] = useState(null);
  const [fuel, setFuel] = useState("");
  const [mileage, setMileage] = useState("");
  const [notes, setNotes] = useState("");
  const [markers, setMarkers] = useState([]);
  const [charges, setCharges] = useState([]);
  const [busy, setBusy] = useState(false);
  // Iter 100 — early check-in: actual return time + post-return vehicle status.
  const [actualAt, setActualAt] = useState("");
  const [statusAfter, setStatusAfter] = useState("available");

  useEffect(() => {
    if (!open) return;
    setStep(0); setChecklist(null); setCharges([]); setBusy(false);
    setStatusAfter("available");
    const now = new Date();
    now.setMinutes(now.getMinutes() - now.getTimezoneOffset());
    setActualAt(now.toISOString().slice(0, 16));
    api.get(`/bookings/${booking.id}/checklist`).then((r) => {
      const rt = r.data.return || {};
      setChecklist(r.data);
      setFuel(rt.fuel_level || "");
      setMileage(rt.mileage_reading ?? "");
      setNotes(rt.notes || "");
      setMarkers(rt.damage_markers || []);
    }).catch((e) => toast.error(formatApiError(e)));
  }, [open, booking]);

  if (!open) return null;

  const saveChecklist = async (fields) => {
    try {
      const { data } = await api.patch(`/bookings/${booking.id}/checklist`, { phase: "return", ...fields });
      setChecklist(data);
      return data;
    } catch (e) {
      toast.error(formatApiError(e));
      return null;
    }
  };

  const addCharge = (kind) => setCharges((c) => [...c, { kind, label: kind, amount: "" }]);
  const setChargeAmount = (i, v) => setCharges((c) => c.map((x, ix) => (ix === i ? { ...x, amount: v } : x)));
  const setChargeLabel = (i, v) => setCharges((c) => c.map((x, ix) => (ix === i ? { ...x, label: v } : x)));
  const removeCharge = (i) => setCharges((c) => c.filter((_, ix) => ix !== i));
  const chargesTotal = charges.reduce((s, c) => s + (Number(c.amount) || 0), 0);
  const deposit = Number(booking.deposit_amount || 0);
  const refund = Math.max(0, deposit - chargesTotal);

  const next = async () => {
    setBusy(true);
    try {
      if (step === 0) {
        const data = await saveChecklist({ mileage_reading: mileage === "" ? undefined : mileage });
        if (!data) return;
        const ov = data.mileage_overage;
        if (ov?.overage_eur > 0 && !charges.some((c) => c.kind === "Extra mileage")) {
          setCharges((c) => [...c, { kind: "Extra mileage", label: `Extra mileage (${ov.overage_km} km)`, amount: String(ov.overage_eur) }]);
          toast.info(`Mileage overage detected: ${ov.overage_km} km → €${ov.overage_eur} (added to charges)`);
        }
      } else if (step === 1) {
        if (!(await saveChecklist({ fuel_level: fuel }))) return;
      } else if (step === 2) {
        if (!(await saveChecklist({ damage_markers: markers, notes }))) return;
      } else if (step === 4) {
        await api.post(`/bookings/${booking.id}/return/complete`, {
          extra_charges: charges
            .filter((c) => Number(c.amount) > 0)
            .map((c) => ({ label: c.label || c.kind, amount: Number(c.amount), kind: c.kind })),
          notes: notes || null,
          actual_return_at: actualAt ? new Date(actualAt).toISOString() : null,
          vehicle_status_after: statusAfter,
        });
        toast.success("Return completed — booking closed ✅");
        onDone?.();
        return;
      }
      setStep((s) => Math.min(s + 1, STEPS.length - 1));
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const pk = checklist?.pickup || {};

  return (
    <WizardShell open={open} onClose={onClose} title={`Return — ${booking.car?.make} ${booking.car?.model}`} step={step} steps={STEPS} testId="return-wizard">
      {step === 0 && (
        <div className="space-y-4" data-testid="return-step-mileage">
          <Row label="Mileage at pickup" value={pk.mileage_reading != null ? `${pk.mileage_reading} km` : "not recorded"} />
          <div>
            <div className="text-sm font-bold mb-2">Return mileage (km)</div>
            <Input type="number" inputMode="numeric" value={mileage} onChange={(e) => setMileage(e.target.value)} placeholder="e.g. 45850" className="rounded-xl h-12 text-lg" data-testid="return-mileage" />
          </div>
        </div>
      )}

      {step === 1 && (
        <div className="space-y-4" data-testid="return-step-fuel">
          <Row label="Fuel at pickup" value={pk.fuel_level || "not recorded"} />
          <div>
            <div className="text-sm font-bold mb-2">Fuel level at return</div>
            <FuelPicker value={fuel} onChange={setFuel} testIdPrefix="return-fuel" />
          </div>
        </div>
      )}

      {step === 2 && (
        <div className="space-y-4" data-testid="return-step-inspection">
          <div className="text-sm text-neutral-500">Pickup damage shows dimmed. Tap to mark <strong>new</strong> scratches, dents or accident damage.</div>
          <DamageDiagramCanvas
            bodyType={pk.damage_body_type || checklist?.body_type}
            markers={markers}
            onChange={setMarkers}
            canEdit
            testId="return-wizard-diagram"
            compareMarkers={pk.damage_markers || []}
            compareLabel="Pickup"
          />
          <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Return notes (optional)" className="rounded-xl" data-testid="return-notes" />
        </div>
      )}

      {step === 3 && (
        <div className="space-y-4" data-testid="return-step-charges">
          <div className="text-sm font-bold">Add extra charges</div>
          <div className="flex flex-wrap gap-1.5">
            {CHARGE_KINDS.map((k) => (
              <button key={k} type="button" onClick={() => addCharge(k)} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full border border-border bg-white text-xs font-bold" data-testid={`add-charge-${k.toLowerCase().replace(/\s+/g, "-")}`}>
                <Plus className="h-3 w-3" /> {k}
              </button>
            ))}
          </div>
          <div className="space-y-2">
            {charges.map((c, i) => (
              <div key={i} className="flex items-center gap-2" data-testid={`charge-row-${i}`}>
                <Input value={c.label} onChange={(e) => setChargeLabel(i, e.target.value)} className="rounded-xl flex-1" />
                <Input type="number" inputMode="decimal" value={c.amount} onChange={(e) => setChargeAmount(i, e.target.value)} placeholder="€" className="rounded-xl w-24" data-testid={`charge-amount-${i}`} />
                <button type="button" onClick={() => removeCharge(i)} className="text-neutral-400" aria-label="Remove"><Trash2 className="h-4 w-4" /></button>
              </div>
            ))}
            {charges.length === 0 && <div className="text-sm text-neutral-400">No extra charges.</div>}
          </div>
          <Row label="Charges total" value={<Money amount={chargesTotal} />} strong />
        </div>
      )}

      {step === 4 && (
        <div className="space-y-2" data-testid="return-step-deposit">
          <Row label="Deposit received" value={<Money amount={deposit} />} />
          <Row label="Deductions (charges)" value={<Money amount={chargesTotal} />} />
          <Row label="Deposit refund" value={<Money amount={refund} />} strong />
          {chargesTotal > deposit && (
            <div className="rounded-xl bg-amber-50 border border-amber-200 text-amber-800 text-xs font-semibold p-3">
              Charges exceed the deposit by <Money amount={chargesTotal - deposit} /> — collect the difference from the customer.
            </div>
          )}
          {/* Iter 100 — actual return time (early check-in) + post-return status. */}
          <div className="pt-2">
            <div className="text-sm font-bold mb-1.5">Actual return date & time</div>
            <Input type="datetime-local" value={actualAt} onChange={(e) => setActualAt(e.target.value)} className="rounded-xl h-11" data-testid="actual-return-at" />
            <div className="text-xs text-neutral-500 mt-1">If earlier than the scheduled return, the vehicle becomes bookable again from this time. The scheduled date stays on record.</div>
          </div>
          <div className="pt-1">
            <div className="text-sm font-bold mb-1.5">Vehicle status after return</div>
            <div className="grid grid-cols-2 gap-1.5">
              {[
                ["available", "Available immediately"],
                ["cleaning_required", "Cleaning required"],
                ["maintenance_required", "Maintenance required"],
                ["damage_inspection", "Damage inspection"],
                ["accident", "Accident / Out of service"],
                ["blocked", "Manually blocked"],
              ].map(([k, label]) => (
                <button key={k} type="button" onClick={() => setStatusAfter(k)} className={`rounded-xl border py-2.5 px-2 text-xs font-bold text-left ${statusAfter === k ? "bg-neutral-900 text-white border-neutral-900" : "bg-white border-border text-neutral-600"}`} data-testid={`status-after-${k}`}>
                  {label}
                </button>
              ))}
            </div>
          </div>
          <div className="text-xs text-neutral-500 pt-2">
            Completing the return closes this booking. Early returns do not refund automatically — handle any price adjustment per your rental policy.
          </div>
        </div>
      )}

      <WizardNav
        step={step}
        lastStep={STEPS.length - 1}
        onBack={() => setStep((s) => Math.max(0, s - 1))}
        onNext={next}
        busy={busy}
        nextLabel={step === STEPS.length - 1 ? "Complete Return" : "Next"}
        nextTestId={step === STEPS.length - 1 ? "complete-return-btn" : "wizard-next"}
      />
    </WizardShell>
  );
}
